import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Layout from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { ArrowRight, ShieldCheck, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import { MarkdownRenderer } from "@/components/shared/MarkdownRenderer";
import { getYouTubeInfo } from "@/lib/utils";
import { SEO } from "@/components/SEO";

const PartnerDetails = () => {
  const { slug } = useParams<{ slug: string }>();

  const { data: partner, isLoading } = useQuery({
    queryKey: ['partner', slug],
    queryFn: async (): Promise<any> => {
      const { data, error } = await supabase
        .from('partners' as any)
        .select('*')
        .eq('slug', slug)
        .eq('is_active', true)
        .single();
        
      if (error) throw error;
      return data;
    },
    enabled: !!slug
  });

  if (isLoading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex justify-center items-center">
          <Loader2 className="w-8 h-8 animate-spin text-slate-300" />
        </div>
      </Layout>
    );
  }

  if (!partner) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex flex-col justify-center items-center text-center">
          <AlertCircle className="w-12 h-12 text-slate-300 mb-4" />
          <h1 className="text-xl font-bold text-slate-900">הספק לא נמצא</h1>
          <Button asChild variant="link" className="mt-4">
            <Link to="/">חזרה לדף הבית</Link>
          </Button>
        </div>
      </Layout>
    );
  }

  const p = partner as any;

  // Calculate Video Embed (YouTube or Direct)
  const videoUrl = p.video_url;
  const { embedUrl: youtubeEmbed, isValid: hasYoutube } = getYouTubeInfo(videoUrl);
  const isDirectVideo = videoUrl?.match(/\.(mp4|webm|mov|m4v)$/i);

  return (
    <Layout>
      <SEO 
        title={`${p.name} - פתרונות ומומחים`}
        description={p.short_desc}
        image={p.logo_url}
        type="website"
        url={window.location.href}
      />
      <div className="bg-white min-h-screen pb-20">
        {/* Header / Hero */}
        <div className="pt-32 pb-16 border-b border-slate-100 bg-slate-50/30">
          <div className="container mx-auto px-6 max-w-4xl">
            <Link to="/solutions" className="inline-flex items-center text-sm text-slate-500 hover:text-slate-900 mb-8 font-medium transition-colors">
              <ArrowRight className="h-4 w-4 ms-2" />
              חזרה לפתרונות למערכות
            </Link>

            <div className="text-center">
              <div className="inline-flex items-center justify-center p-6 bg-white rounded-2xl shadow-sm border border-slate-100 mb-8">
                <img 
                  src={p.logo_url} 
                  alt={p.name} 
                  className="h-16 w-auto object-contain"
                />
              </div>
              
              <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight mb-4">
                {p.name}
              </h1>
              <p className="text-xl text-slate-500 font-light max-w-2xl mx-auto leading-relaxed">
                {p.short_desc}
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="container mx-auto px-6 max-w-3xl py-16">
          
          {/* Video Section - Prominently Displayed */}
          {((hasYoutube && youtubeEmbed) || isDirectVideo) && (
            <div className="mb-16 animate-in fade-in slide-in-from-bottom-4 duration-700">
              <div className="aspect-video w-full rounded-2xl overflow-hidden border border-slate-200 bg-black shadow-lg relative">
                {hasYoutube ? (
                  <iframe
                    src={youtubeEmbed}
                    title="YouTube video player"
                    className="w-full h-full border-none"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                ) : (
                  <video
                    src={videoUrl}
                    controls
                    className="w-full h-full"
                    playsInline
                  />
                )}
              </div>
            </div>
          )}

          <div className="bg-white">
            <MarkdownRenderer content={p.full_desc} />
          </div>

          {/* Verification Badge */}
          <div className="mt-12 p-6 bg-blue-50/50 rounded-2xl border border-blue-100 flex items-start gap-4">
            <ShieldCheck className="w-6 h-6 text-blue-600 shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-slate-900 text-sm mb-1">שירות מומלץ</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                חברת {p.name} נבדקה על ידי צוות FeeVho ונמצאה כגורם מקצועי ואמין. 
                אנו ממליצים עליהם לכל מי שמחפש שירותים משלימים למערכת שלו.
              </p>
            </div>
          </div>

          {/* CTA Actions */}
          <div className="mt-12 flex flex-col sm:flex-row gap-4 pt-8 border-t border-slate-100">
            {p.website_url && (
              <Button size="lg" className="h-14 px-8 rounded-xl bg-slate-900 hover:bg-slate-800 text-white gap-2 text-base font-medium shadow-lg shadow-slate-200" asChild>
                <a href={p.website_url} target="_blank" rel="noopener noreferrer">
                  מעבר לאתר החברה
                  <ExternalLink className="w-4 h-4 opacity-70" />
                </a>
              </Button>
            )}
            
            <Button size="lg" variant="outline" className="h-14 px-8 rounded-xl border-slate-200 hover:bg-slate-50 text-slate-900 gap-2 text-base" asChild>
              <Link to="/solutions">
                <ArrowRight className="w-4 h-4" />
                חזרה לפתרונות
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PartnerDetails;